/**
 * Modulo Convenzioni Aziendali - Flusso Cliente
 * 
 * Pagine incluse:
 * - ConvenzioneRegisterPage: form registrazione con codice opzionale (Lead)
 * - ConvenzioneCompleteProfilePage: anagrafica estesa (Lead -> Prospect)
 * - ConvenzioneFunnelPage: pannello "Personalizza la mia auto"
 * - ConvenzioneCatalogPage: card catalogo con Visto Verde
 * 
 * Rotta base: /convenzioni/*
 */
import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AyvensQuestion } from '../components/ayvens/AyvensQuestion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, Loader2, Building2, ArrowRight, Gauge, Calendar, Fuel, Wallet, ShieldCheck, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_KEYS = ['conv_token', 'conv_user'];

// Sessione nel cookie httpOnly impostato dal server: il token non è accessibile da JavaScript
const useConvenzioneAuth = () => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/convenzioni/me`);
      setUser(res.data);
      return res.data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setReady(true);
    }
  }, []);
  useEffect(() => {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
    refreshUser();
  }, [refreshUser]);
  const setSession = (_t, u) => {
    if (!u) axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(u);
  };
  const authHeader = () => ({});
  // 'pending' finché la sessione non è verificata, per non reindirizzare prima del tempo
  const token = ready ? (user ? 'session' : null) : 'pending';
  return { token, user, setSession, authHeader, refreshUser };
};

// ============================================================
// LAYOUT COMUNE
// ============================================================
const ConvenzioneLayout = ({ children, auth }) => {
  const navigate = useNavigate();
  const conv = auth.user?.convenzione;
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logos/logo-light.png" alt="MaCheNoleggio" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
            {conv?.logo_url && (
              <>
                <span className="text-slate-300">×</span>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${conv.logo_url}`} alt={conv.nome} className="h-8 w-auto object-contain" />
              </>
            )}
            {conv && !conv.logo_url && (
              <span className="text-sm text-slate-600 pl-2 border-l">
                <Building2 className="w-4 h-4 inline mr-1 text-slate-400" />
                {conv.nome}
              </span>
            )}
          </div>
          {auth.user && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 hidden sm:block">{auth.user.email}</span>
              <Button variant="ghost" size="sm" onClick={() => { auth.setSession(null, null); navigate('/convenzioni/register'); }} className="gap-1">
                <LogOut className="w-3.5 h-3.5" /> Esci
              </Button>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

// ============================================================
// REGISTER (Lead)
// ============================================================
const ConvenzioneRegisterPage = ({ auth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    nome: '', cognome: '', email: '', telefono: '',
    password: '', conferma_password: '',
    gia_cliente_ayvens: null,
    codice_convenzione: new URLSearchParams(location.search).get('codice') || '',
  });
  const [convenzionePreview, setConvenzionePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codiceLoading, setCodiceLoading] = useState(false);
  // Fase 1.4: schermata di conferma "in attesa di approvazione"
  const [pendingSuccess, setPendingSuccess] = useState(null);

  const verifyCodice = async (cod) => {
    if (!cod || cod.length < 4) { setConvenzionePreview(null); return; }
    setCodiceLoading(true);
    try {
      const res = await axios.get(`${API}/public/convenzioni/verify`, { params: { codice: cod } });
      setConvenzionePreview(res.data);
    } catch {
      setConvenzionePreview(null);
    } finally {
      setCodiceLoading(false);
    }
  };

  useEffect(() => {
    if (form.codice_convenzione) {
      const timer = setTimeout(() => verifyCodice(form.codice_convenzione.toUpperCase()), 500);
      return () => clearTimeout(timer);
    }
    setConvenzionePreview(null);
  }, [form.codice_convenzione]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.conferma_password) {
      toast.error('Le password non coincidono');
      return;
    }
    if (form.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    if (form.gia_cliente_ayvens === null) {
      toast.error('Indica se sei già cliente Ayvens / ALD Automotive');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, codice_convenzione: form.codice_convenzione?.toUpperCase() || undefined };
      const res = await axios.post(`${API}/public/convenzioni/register`, payload);
      // Fase 1.4: nessun auto-login. L'account è in attesa di approvazione admin.
      setPendingSuccess({
        message: res.data?.message,
        ayvensMessage: res.data?.ayvens_message,
        convenzione: res.data?.user?.convenzione,
        email: res.data?.user?.email,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (pendingSuccess) {
    return (
      <ConvenzioneLayout auth={auth}>
        <div className="max-w-lg mx-auto" data-testid="convenzione-register-pending">
          <Card>
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <CardTitle>Iscrizione ricevuta!</CardTitle>
              <CardDescription>
                Il tuo account per <strong>{pendingSuccess.convenzione?.nome || 'la convenzione'}</strong> è
                stato creato ed è ora <strong>in fase di verifica</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                Riceverai un'email a <strong>{pendingSuccess.email}</strong> non appena il tuo profilo
                sarà abilitato dall'amministrazione. Da quel momento potrai accedere con le credenziali
                che hai appena scelto.
              </div>
              {pendingSuccess.ayvensMessage && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" data-testid="convenzione-ayvens-message">
                  {pendingSuccess.ayvensMessage}
                </div>
              )}
              <Button className="w-full" onClick={() => navigate('/login')} data-testid="btn-goto-login">
                Vai al Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </ConvenzioneLayout>
    );
  }

  return (
    <ConvenzioneLayout auth={auth}>
      <div className="max-w-lg mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Registrati alla Convenzione</CardTitle>
            <CardDescription>Crea il tuo account. Se hai un codice convenzione inseriscilo, altrimenti sarai associato a Machenoleggio.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="convenzione-register-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome *</Label>
                  <Input required value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} data-testid="input-nome" />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input required value={form.cognome} onChange={e => setForm({ ...form, cognome: e.target.value })} data-testid="input-cognome" />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
              </div>
              <div>
                <Label>Telefono *</Label>
                <Input required value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} data-testid="input-telefono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Password *</Label>
                  <Input type="password" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="input-password" />
                </div>
                <div>
                  <Label>Conferma Password *</Label>
                  <Input type="password" required value={form.conferma_password} onChange={e => setForm({ ...form, conferma_password: e.target.value })} data-testid="input-conferma-password" />
                </div>
              </div>
              <AyvensQuestion value={form.gia_cliente_ayvens} onChange={(v) => setForm({ ...form, gia_cliente_ayvens: v })} testIdPrefix="convenzione-ayvens" />
              <div>
                <Label>Codice Convenzione (opzionale)</Label>
                <Input
                  value={form.codice_convenzione}
                  onChange={e => setForm({ ...form, codice_convenzione: e.target.value.toUpperCase() })}
                  placeholder="Inserisci codice"
                  data-testid="input-codice"
                />
                {codiceLoading && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Verifica codice...</p>}
                {convenzionePreview && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Convenzione: <strong>{convenzionePreview.nome}</strong>
                  </p>
                )}
                {form.codice_convenzione && !convenzionePreview && !codiceLoading && (
                  <p className="text-xs text-amber-600 mt-1">Codice non riconosciuto - se lasciato invariato sarà usato Machenoleggio</p>
                )}
              </div>
              <Button type="submit" disabled={loading} className="w-full mt-2" data-testid="btn-register">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Crea Account
              </Button>
              <p className="text-xs text-center text-slate-500">
                Hai già un account? <Link to="/login" className="text-blue-600 hover:underline">Accedi</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </ConvenzioneLayout>
  );
};

// ============================================================
// COMPLETE PROFILE (Lead -> Prospect)
// ============================================================
const ConvenzioneCompleteProfilePage = ({ auth }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    codice_fiscale: '', indirizzo: '', citta: '', cap: '', provincia: '', data_nascita: '',
    profilo_fiscale: 'dipendente',
    ragione_sociale: '', partita_iva: '', codice_fiscale_azienda: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!auth.token) navigate('/convenzioni/register');
    if (auth.user?.profilo_completo) navigate('/convenzioni/funnel');
  }, [auth.token, auth.user, navigate]);

  const isBusiness = ['piva_forfettaria', 'piva_ordinaria', 'ditta_individuale', 'societa'].includes(form.profilo_fiscale);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/convenzioni/complete-profile`, form, { headers: auth.authHeader() });
      await auth.refreshUser();
      toast.success('Profilo completato! Ora puoi personalizzare la ricerca');
      navigate('/convenzioni/funnel');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio profilo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConvenzioneLayout auth={auth}>
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Completa la tua Anagrafica</CardTitle>
            <CardDescription>
              Per accedere al catalogo Convenzione devi completare i dati fiscali e di contatto.
              <Badge className="ml-2 bg-amber-100 text-amber-700">Lead → Prospect</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="complete-profile-form">
              <div>
                <Label>Profilo Fiscale *</Label>
                <Select value={form.profilo_fiscale} onValueChange={v => setForm({ ...form, profilo_fiscale: v })}>
                  <SelectTrigger data-testid="select-profilo-fiscale"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dipendente">Dipendente</SelectItem>
                    <SelectItem value="piva_forfettaria">P.IVA Forfettaria</SelectItem>
                    <SelectItem value="piva_ordinaria">P.IVA Ordinaria</SelectItem>
                    <SelectItem value="ditta_individuale">Ditta Individuale</SelectItem>
                    <SelectItem value="societa">Società</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input value={form.codice_fiscale} onChange={e => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} data-testid="input-cf" />
                </div>
                <div>
                  <Label>Data di Nascita</Label>
                  <Input type="date" value={form.data_nascita} onChange={e => setForm({ ...form, data_nascita: e.target.value })} data-testid="input-data-nascita" />
                </div>
              </div>

              <div>
                <Label>Indirizzo *</Label>
                <Input required value={form.indirizzo} onChange={e => setForm({ ...form, indirizzo: e.target.value })} data-testid="input-indirizzo" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Città *</Label>
                  <Input required value={form.citta} onChange={e => setForm({ ...form, citta: e.target.value })} data-testid="input-citta" />
                </div>
                <div>
                  <Label>CAP *</Label>
                  <Input required maxLength={5} value={form.cap} onChange={e => setForm({ ...form, cap: e.target.value })} data-testid="input-cap" />
                </div>
              </div>
              <div>
                <Label>Provincia *</Label>
                <Input required maxLength={2} value={form.provincia} onChange={e => setForm({ ...form, provincia: e.target.value.toUpperCase() })} placeholder="Es. MI" data-testid="input-provincia" />
              </div>

              {isBusiness && (
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-semibold text-slate-700">Dati Aziendali</p>
                  {form.profilo_fiscale === 'societa' && (
                    <div>
                      <Label>Ragione Sociale *</Label>
                      <Input required value={form.ragione_sociale} onChange={e => setForm({ ...form, ragione_sociale: e.target.value })} data-testid="input-ragione-sociale" />
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Partita IVA *</Label>
                      <Input required value={form.partita_iva} onChange={e => setForm({ ...form, partita_iva: e.target.value })} data-testid="input-piva" />
                    </div>
                    <div>
                      <Label>Codice Fiscale Azienda</Label>
                      <Input value={form.codice_fiscale_azienda} onChange={e => setForm({ ...form, codice_fiscale_azienda: e.target.value.toUpperCase() })} data-testid="input-cf-azienda" />
                    </div>
                  </div>
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full" data-testid="btn-complete-profile">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salva e Continua
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </ConvenzioneLayout>
  );
};

// ============================================================
// FUNNEL "Personalizza la mia auto"
// ============================================================
const DURATE = [24, 36, 48, 60];
const KM_OPTS = [10000, 15000, 20000, 25000, 30000];
const ALIMENTAZIONI = ['Benzina', 'Diesel', 'Ibrido', 'Elettrico', 'GPL', 'Metano'];
const PROFILI = [
  { v: 'dipendente', l: 'Dipendente' },
  { v: 'piva_forfettaria', l: 'P.IVA Forfettaria' },
  { v: 'piva_ordinaria', l: 'P.IVA Ordinaria' },
  { v: 'ditta_individuale', l: 'Ditta Individuale' },
  { v: 'societa', l: 'Società' },
];

const ConvenzioneFunnelPage = ({ auth }) => {
  const navigate = useNavigate();
  const [params, setParams] = useState(() => {
    const saved = sessionStorage.getItem('conv_funnel_params');
    return saved ? JSON.parse(saved) : {
      durata_mesi: 48, km_annui: 15000, alimentazione: 'Ibrido',
      anticipo_lordo: 0, profilo_fiscale: auth.user?.profilo_fiscale || 'dipendente',
    };
  });

  useEffect(() => {
    if (!auth.token) navigate('/convenzioni/register');
    // Se il profilo non è completato, forza il redirect
    if (auth.user && auth.user.stato_utente === 'lead') navigate('/convenzioni/complete-profile');
  }, [auth.token, auth.user, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    sessionStorage.setItem('conv_funnel_params', JSON.stringify(params));
    navigate('/convenzioni/catalogo');
  };

  return (
    <ConvenzioneLayout auth={auth}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Personalizza la tua Auto</h1>
          <p className="text-slate-500 mt-2">Rispondi alle domande, ti mostriamo il catalogo su misura per te</p>
        </div>
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="space-y-5" data-testid="funnel-form">
              <div>
                <Label className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Durata desiderata *</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {DURATE.map(d => (
                    <Button
                      key={d}
                      type="button"
                      variant={params.durata_mesi === d ? 'default' : 'outline'}
                      onClick={() => setParams({ ...params, durata_mesi: d })}
                      data-testid={`durata-${d}`}
                    >
                      {d} mesi
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-1"><Gauge className="w-4 h-4" /> Chilometri annui *</Label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {KM_OPTS.map(k => (
                    <Button
                      key={k}
                      type="button"
                      variant={params.km_annui === k ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setParams({ ...params, km_annui: k })}
                      data-testid={`km-${k}`}
                    >
                      {k.toLocaleString('it-IT')}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-1"><Fuel className="w-4 h-4" /> Tipologia motore *</Label>
                <Select value={params.alimentazione} onValueChange={v => setParams({ ...params, alimentazione: v })}>
                  <SelectTrigger data-testid="select-alimentazione"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALIMENTAZIONI.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="flex items-center gap-1"><Wallet className="w-4 h-4" /> Anticipo Lordo (IVA inclusa) *</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={params.anticipo_lordo}
                  onChange={e => setParams({ ...params, anticipo_lordo: Number(e.target.value) })}
                  placeholder="Es. 3.660"
                  data-testid="input-anticipo"
                />
                {params.anticipo_lordo > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Imponibile (scorporo IVA 22%): <strong>€{(params.anticipo_lordo / 1.22).toFixed(2)}</strong>
                  </p>
                )}
              </div>

              <div>
                <Label>Profilo Fiscale (Target) *</Label>
                <Select value={params.profilo_fiscale} onValueChange={v => setParams({ ...params, profilo_fiscale: v })}>
                  <SelectTrigger data-testid="select-target"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROFILI.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full h-11 text-base" data-testid="btn-search">
                Trova la mia auto
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </ConvenzioneLayout>
  );
};

// ============================================================
// CATALOGO CARD (con Visto Verde placeholder)
// ============================================================
const ConvenzioneCatalogPage = ({ auth }) => {
  const navigate = useNavigate();
  const [risultati, setRisultati] = useState([]);
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vistiVerdi, setVistiVerdi] = useState(new Set());

  useEffect(() => {
    if (!auth.token) { navigate('/convenzioni/register'); return; }
    const saved = sessionStorage.getItem('conv_funnel_params');
    if (!saved) { navigate('/convenzioni/funnel'); return; }
    const p = JSON.parse(saved);
    setParams(p);
    (async () => {
      try {
        const [searchRes, vvRes] = await Promise.all([
          axios.post(`${API}/convenzioni/funnel/search`, p, { headers: auth.authHeader() }),
          axios.get(`${API}/convenzioni/visto-verde`, { headers: auth.authHeader() }).catch(() => ({ data: [] })),
        ]);
        setRisultati(searchRes.data.risultati || []);
        setVistiVerdi(new Set((vvRes.data || []).map(v => v.vehicle_id)));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Errore ricerca catalogo');
        if (err.response?.status === 403) navigate('/convenzioni/complete-profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, navigate]);

  const toggleVistoVerde = async (vehicleId) => {
    try {
      const res = await axios.post(`${API}/convenzioni/visto-verde`,
        { vehicle_id: vehicleId, funnel_params: params },
        { headers: auth.authHeader() });
      setVistiVerdi(prev => {
        const next = new Set(prev);
        if (res.data.attivo) next.add(vehicleId); else next.delete(vehicleId);
        return next;
      });
      toast.success(res.data.attivo ? 'Visto Verde attivato' : 'Visto Verde rimosso');
    } catch {
      toast.error('Errore Visto Verde');
    }
  };

  return (
    <ConvenzioneLayout auth={auth}>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Auto disponibili per te</h1>
          {params && (
            <p className="text-sm text-slate-500 mt-1">
              {params.durata_mesi} mesi • {params.km_annui.toLocaleString('it-IT')} km/anno •{' '}
              {params.alimentazione} • Anticipo netto €{(params.anticipo_lordo / 1.22).toFixed(0)}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/convenzioni/funnel')}>Modifica ricerca</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : risultati.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          Nessun veicolo trovato con questi criteri. <br />
          <Button variant="link" onClick={() => navigate('/convenzioni/funnel')}>Modifica la ricerca</Button>
        </CardContent></Card>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-3">{risultati.length} veicoli trovati</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {risultati.slice(0, 60).map(r => (
              <VeicoloCard
                key={r.vehicle_id}
                v={r}
                vistoVerde={vistiVerdi.has(r.vehicle_id)}
                onToggleVistoVerde={() => toggleVistoVerde(r.vehicle_id)}
              />
            ))}
          </div>
          {risultati.length > 60 && (
            <p className="text-center text-xs text-slate-400 mt-6">Mostrati i primi 60 risultati su {risultati.length}. Affina la ricerca per vedere altri veicoli.</p>
          )}
        </>
      )}
    </ConvenzioneLayout>
  );
};

const VeicoloCard = ({ v, vistoVerde, onToggleVistoVerde }) => {
  const isUsato = (v.tipo || '').toLowerCase() === 'usato';
  // Fallback immagine: link salvato → hook Drive (marca+modello) → placeholder
  return (
    <VeicoloCardInner v={v} isUsato={isUsato} vistoVerde={vistoVerde} onToggleVistoVerde={onToggleVistoVerde} />
  );
};

const VeicoloCardInner = ({ v, isUsato, vistoVerde, onToggleVistoVerde }) => {
  const [imgFallback, setImgFallback] = useState(null);
  useEffect(() => {
    if (v.link_immagine || !v.marca || !v.modello) return;
    // Chiamata al catalog pubblico per recuperare l'immagine Drive
    axios.get(`${API}/public/vehicle-image`, { params: { marca: v.marca, modello: v.modello } })
      .then(res => setImgFallback(res.data?.image_url || null))
      .catch(() => {});
  }, [v.link_immagine, v.marca, v.modello]);
  const imgSrc = v.link_immagine || imgFallback;
  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-shadow ${isUsato ? 'ring-1 ring-slate-200' : ''}`} data-testid={`veicolo-card-${v.vehicle_id}`}>
      <div className="h-40 bg-slate-100 flex items-center justify-center overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={`${v.marca} ${v.modello}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <span className="text-slate-300 text-xs">Nessuna immagine</span>
        )}
      </div>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs text-slate-500">{v.marca}</p>
            <p className="text-lg font-bold leading-tight">{v.modello}</p>
            {v.versione && <p className="text-xs text-slate-600 line-clamp-1">{v.versione}</p>}
          </div>
          <Badge className={isUsato ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-700'}>{isUsato ? 'USATO' : 'NUOVO'}</Badge>
        </div>
        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
          {v.alimentazione && <span>{v.alimentazione}</span>}
          {v.cambio && <span>• {v.cambio}</span>}
        </div>
        <div className="border-t pt-2 mt-2 flex items-end justify-between">
          <div>
            <p className="text-[10px] text-slate-400 uppercase">Canone</p>
            <p className="text-xl font-bold text-slate-800">€{v.canone_base.toFixed(0)}<span className="text-xs font-normal text-slate-500">/mese</span></p>
            <p className="text-[10px] text-slate-400">{v.durata_mesi}m • {v.km_annui.toLocaleString('it-IT')}km</p>
          </div>
          <Button
            size="sm"
            variant={vistoVerde ? 'default' : 'outline'}
            onClick={onToggleVistoVerde}
            className={`gap-1 ${vistoVerde ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
            data-testid={`btn-visto-verde-${v.vehicle_id}`}
            title="Visto Verde"
          >
            {vistoVerde ? <CheckCircle2 className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
            {vistoVerde ? 'Aggiunto' : 'Visto Verde'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// ROOT (routing interno)
// ============================================================
const ConvenzioniModule = () => {
  const auth = useConvenzioneAuth();
  return (
    <Routes>
      <Route path="register" element={<ConvenzioneRegisterPage auth={auth} />} />
      <Route path="complete-profile" element={<ConvenzioneCompleteProfilePage auth={auth} />} />
      <Route path="funnel" element={<ConvenzioneFunnelPage auth={auth} />} />
      <Route path="catalogo" element={<ConvenzioneCatalogPage auth={auth} />} />
      <Route path="*" element={<Navigate to="/convenzioni/register" replace />} />
    </Routes>
  );
};

export default ConvenzioniModule;
